import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import { Box, CircularProgress, IconButton } from "@mui/material";
import type { ChatAttachment } from "@xop/dashboard-shared";
import { useRef, useState } from "react";

import { useVoiceNotePlayback, VOICE_NOTE_PEAK_COUNT } from "../../lib/chat/attachments.js";

const SPEEDS = [1, 1.5, 2] as const;

/** Even, low bars when we have no decoded waveform (decoding unsupported). */
const FLAT_PEAKS = Array.from({ length: VOICE_NOTE_PEAK_COUNT }, () => 0.4);

function formatClock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * A WhatsApp-style voice-message bubble: a round play/pause, a clickable
 * waveform scrubber (played vs. unplayed split), a time that counts up during
 * playback, and a 1× / 1.5× / 2× speed toggle. Audio plays from the same
 * auth-gated blob → object-URL path as other chat media; the waveform is
 * decoded client-side (flat bars where decoding isn't supported).
 */
export function VoiceMessageBubble({
  attachment,
  mine,
}: {
  attachment: ChatAttachment;
  mine: boolean;
}): JSX.Element {
  const { url, peaks } = useVoiceNotePlayback(attachment.id, attachment.mime);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speedIdx, setSpeedIdx] = useState(0);

  const bars = peaks ?? FLAT_PEAKS;
  const progress = duration > 0 ? current / duration : 0;
  // Idle → show the clip length; playing / seeked → show elapsed (WhatsApp).
  const shownTime = playing || current > 0 ? current : duration;

  function togglePlay(): void {
    const el = audioRef.current;
    if (!el) return;
    // Drive off our own `playing` state (kept in sync by the element's
    // play/pause/ended events) rather than el.paused, which the element only
    // updates asynchronously.
    if (playing) el.pause();
    else void el.play();
  }

  function cycleSpeed(): void {
    const next = (speedIdx + 1) % SPEEDS.length;
    setSpeedIdx(next);
    if (audioRef.current) audioRef.current.playbackRate = SPEEDS[next]!;
  }

  function seekToFraction(fraction: number): void {
    const el = audioRef.current;
    if (!el || !Number.isFinite(el.duration) || el.duration <= 0) return;
    el.currentTime = Math.max(0, Math.min(1, fraction)) * el.duration;
  }

  function onLoadedMetadata(): void {
    const el = audioRef.current;
    if (!el) return;
    // MediaRecorder webm blobs often report Infinity until forced to seek.
    if (el.duration === Infinity) {
      const fix = (): void => {
        el.removeEventListener("timeupdate", fix);
        el.currentTime = 0;
        setDuration(Number.isFinite(el.duration) ? el.duration : 0);
      };
      el.addEventListener("timeupdate", fix);
      el.currentTime = 1e101;
    } else {
      setDuration(el.duration);
    }
  }

  return (
    <Box
      data-testid="chat-voice-note"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        mb: 0.5,
        p: 0.75,
        pr: 1,
        borderRadius: 1.5,
        minWidth: 230,
        maxWidth: 320,
      }}
    >
      <IconButton
        onClick={togglePlay}
        disabled={!url}
        aria-label={playing ? "Pause voice message" : "Play voice message"}
        data-testid="chat-voice-play"
        sx={{
          bgcolor: mine ? "rgba(0,0,0,0.12)" : "chat.accent",
          color: mine ? "text.primary" : "#fff",
          width: 38,
          height: 38,
          flexShrink: 0,
          "&:hover": { bgcolor: mine ? "rgba(0,0,0,0.18)" : "chat.accent", filter: "brightness(0.95)" },
        }}
      >
        {!url ? (
          <CircularProgress size={16} />
        ) : playing ? (
          <PauseRoundedIcon />
        ) : (
          <PlayArrowRoundedIcon />
        )}
      </IconButton>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          data-testid="chat-voice-waveform"
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            seekToFraction(rect.width > 0 ? (e.clientX - rect.left) / rect.width : 0);
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "2px",
            height: 26,
            cursor: url ? "pointer" : "default",
          }}
        >
          {bars.map((p, i) => {
            const played = i / bars.length <= progress;
            return (
              <Box
                key={i}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  height: `${Math.max(12, Math.round(p * 100))}%`,
                  borderRadius: 2,
                  bgcolor: played ? "chat.accent" : "chat.meta",
                  opacity: played ? 1 : 0.45,
                }}
              />
            );
          })}
        </Box>
      </Box>

      <Box
        component="span"
        data-testid="chat-voice-time"
        sx={{ fontSize: 11, color: "chat.meta", fontVariantNumeric: "tabular-nums", minWidth: 30 }}
      >
        {formatClock(shownTime)}
      </Box>

      <Box
        component="button"
        type="button"
        onClick={cycleSpeed}
        data-testid="chat-voice-speed"
        aria-label={`Playback speed ${SPEEDS[speedIdx]}x`}
        sx={{
          flexShrink: 0,
          border: "none",
          cursor: "pointer",
          bgcolor: "action.hover",
          color: "text.secondary",
          fontSize: 11,
          fontWeight: 700,
          borderRadius: 1,
          px: 0.5,
          py: 0.25,
          minWidth: 34,
        }}
      >
        {SPEEDS[speedIdx]}×
      </Box>

      {url && (
        <audio
          ref={audioRef}
          src={url}
          preload="metadata"
          data-testid="chat-voice-audio"
          onLoadedMetadata={onLoadedMetadata}
          onTimeUpdate={() => setCurrent(audioRef.current?.currentTime ?? 0)}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => {
            setPlaying(false);
            setCurrent(0);
          }}
          style={{ display: "none" }}
        />
      )}
    </Box>
  );
}
